<template>
    <div class="agreement-form">
        <div class="agreement-form__intro-container" v-if="organizationLogoUrls">
            <div class="agreement-form__intro">
            <img :src="organizationLogoSrc" :srcset="organizationLogoSrcSet" alt="" class="agreement-form__intro-company-logo" />
            <span class="agreement-form__intro-description" v-html="$t('AgreementForm.welcome_to_eduhouse')" />
            </div>
        </div>
        <div class="agreement-form__container">
            <div class="agreement-form__left-side">
                <h1 class="agreement-form__header" v-html="$t('AgreementForm.organization')"></h1>
                <span class="agreement-form__description" v-html="$t('AgreementForm.sign_up_info')"></span>

                <div class="agreement-form__inputs">
                    <template v-if="!isAgreementForm && isLoggedIn && !signed">
                        <span class="agreement-form__description" v-html="$t('AgreementForm.click_button_info')"></span>
                        <span class="error" v-html="error"></span>
                        <TotemButton @click="signIn" :disabled="sending" color="primary" :label="$t('AgreementForm.join_here')" />
                    </template>

                    <template v-if="isAgreementForm && !isLoggedIn && !signed">
                        <div class="agreement-form__input">
                            <TotemInput v-model="firstName" :hasError="$v.firstName.$error"  @blur.native="$v.firstName.$touch()" :label="$t('General.first_name')" isRequired fullWidth/>
                            <totem-form-finnish-label-error :field="$v.firstName" :label="$t('General.first_name')"/>
                        </div>
                        <div class="agreement-form__input">
                            <TotemInput v-model="lastName" :hasError="$v.lastName.$error" @blur.native="$v.lastName.$touch()" :label="$t('General.last_name')" isRequired fullWidth />
                            <totem-form-finnish-label-error :field="$v.lastName" :label="$t('General.last_name')"/>
                        </div>
                        <div class="agreement-form__input">
                            <TotemInput v-model="userEmail" :hasError="$v.userEmail.$error" @blur.native="$v.userEmail.$touch()" :label="$t('General.email_address')" isRequired fullWidth />
                            <totem-form-finnish-label-error :field="$v.userEmail" :label="$t('General.email_address')" />
                        </div>
                        <div class="agreement-form__input">
                            <TotemSelect showSelectedOption :hasError = "$v.jobTitle.$error"  v-model="$v.jobTitle.$model" :options="sortedJobTitles" :label="$t('General.title')" isRequired width="100%"/>
                            <totem-form-finnish-label-error :field="$v.jobTitle" :label="$t('General.title')"/>
                        </div>
                        <div class="agreement-form__input">
                            <TotemPasswordInput v-model="password" :hasError="$v.password.$error" @blur.native="$v.password.$touch()" :label="$t('General.password')" isRequired fullWidth />
                            <totem-form-finnish-label-error :field="$v.password" :label="$t('General.password')"/>
                        </div>
                        <div class="agreement-form__input">
                            <TotemPasswordInput v-model="passwordRepeat" :hasError="$v.passwordRepeat.$error" @blur.native="$v.passwordRepeat.$touch()" :label="$t('General.repeat_password')" isRequired fullWidth/>
                            <totem-form-finnish-label-error :field="$v.passwordRepeat"/>
                        </div>
                        <div class="agreement-form__terms">
                            <TotemCheckbox class="agreement-form__terms-checkbox" v-model="$v.terms.$model" name="terms"/>
                            <TotemInputLabel isRequired class="agreement-form__terms-label" v-html="$t('General.terms_and_policy_info_updated')">
                            </TotemInputLabel>
                        </div>

                        <totem-form-finnish-label-error :field="$v.terms" v-if="!$v.terms.$model" :label="$t('General.terms_and_policy')"/>
                        <div>
                            <TotemButton
                                @click="sendForm"
                                class="trial-form__submit-button"
                                color="primary"
                                :disabled="sending"
                                :label="$t('AgreementForm.get_started')"
                            />
                        </div>
                        <span class="error">
                            <div v-html="error"></div>
                            <router-link v-if="alreadyExists" :to="{name:'new-password'}" class="agreement-form__link agreement-form__change-pass-button">{{ $t("Login.forgot_password") }}</router-link>
                        </span>
                    </template>

                    <template v-if="isAgreementForm && signed && !isLoggedIn">
                        <span class="agreement-form__thank-you" v-html="$t('AgreementForm.thank_you_for_order_info')"></span>
                    </template>

                    <template v-if="isAgreementForm && signed && isLoggedIn">
                        <span class="agreement-form__description" v-html="$t('AgreementForm.thank_you_for_order')"></span>
                        <TotemButton @click="goToSummaryPage" :label="$t('AgreementForm.go_to_summary_page')" />
                    </template>
                </div>
            </div>

            <div class="agreement-form__right-side">
                <div class="agreement-form__inner">
                    <template v-if="!isAgreementForm && error">
                        <span class="error">
                            {{ error }}
                        </span>
                    </template>
                    <img src="@/assets/images/agreement-form.png" class="agreement-form__image" alt="" v-if="isDesktopView" />
                </div>
            </div>
        </div>
        <template v-if="isAgreementForm && !isLoggedIn && !signed">
            <div class="agreement-form__description-login-notification">
                <h2 class="agreement-form__description-login-notification-title" v-html="$t('AgreementForm.sign_in_title')"></h2>
                <span v-html="$t('AgreementForm.sign_in')"></span>
                <span class="agreement-form__link" @click="openLoginBox">{{ $t('AgreementForm.here') }}</span>
            </div>
        </template>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { validationMixin } from 'vuelidate'
    import sortAlphabeticallyAscByJobTitle from 'components/shared/mixins/sorting/sortAlphabeticallyAscByJobTitle'
    const { validName } = require('components/shared/global-forms/custom-validations.js')
    const { required, minLength, email, sameAs } = require('vuelidate/lib/validators')

    export default {
        name: 'agreement-form',
        mixins: [ validationMixin, sortAlphabeticallyAscByJobTitle ],
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                language: 'getLanguage'
            }),
            jobTitleSelectWidth () {
                return this.$isLangSe ? 330 : 285
            },
            organizationLogoSrcSet () {
                return this.organizationLogoUrls ? [
                    this.organizationLogoUrls['x1'],
                    `${this.organizationLogoUrls['x2']} 2x`
                ].join(', ') : null
            },
            organizationLogoSrc () {
                return this.organizationLogoUrls ? `${this.organizationLogoUrls['x1']}` : null
            }
        },
        validations: {
            firstName: {
                required,
                validName
            },
            lastName: {
                required,
                validName
            },
            userEmail: {
                required,
                email
            },
            password: {
                required,
                minLength: minLength(8)
            },
            passwordRepeat: {
                repeatPassword: sameAs('password')
            },
            terms: {
                required
            },
            jobTitle: {
                required
            }
        },
        data () {
            return {
                firstName: null,
                lastName: null,
                userEmail: null,
                password: null,
                jobTitle: null,
                passwordRepeat: null,
                triedSubmit: false,
                error: null,
                terms: null,
                jobTitles: [],
                sortedJobTitles: [],
                isAgreementForm: false,
                signed: false,
                sending: false,
                alreadyExists: false,
                organizationLogoUrls: null
            }
        },
        methods: {
            openLoginBox () {
                this.$store.dispatch('User/toggleLoginBox')
            },
            handleError (code, responseMessage) {
                this.sending = false
                if (code === 621) {
                    this.error = this.$t('AgreementForm.Error.MemberExistsPleaseLogIn')
                    this.alreadyExists = true
                } else if (code === 1111) {
                    this.error = this.$t('AgreementForm.Error.AgreementExpired')
                } else if (code === 1112) {
                    this.error = this.$t('AgreementForm.Error.AgreementLimitReached')
                } else if (code === 1114) {
                    this.error = this.$t('AgreementForm.Error.OrganizationDoNotMatch')
                } else if (code === 1113) {
                    this.error = this.$t('AgreementForm.Error.StillActivePersonalOrders')
                } else if (code === 'ERR_BAD_REQUEST') {
                    this.error = this.$t(responseMessage)
                } else {
                    this.error = this.$t('General.sth_went_wrong')
                }
            },
            goToSummaryPage () {
                this.$router.push({ 'name': 'home-courses' })
            },
            async signIn () {
                try {
                    this.alreadyExists = false
                    this.sending = true
                    await this.$store.dispatch('User/sendAgreementRequestForExisting', {
                        slug: this.$route.params.slug,
                    })
                    this.signed = true
                } catch (err) {
                    this.handleError(err.code)
                }
            },
            async sendForm (e) {
                this.alreadyExists = false
                this.error = null
                this.$v.$touch()
                if (this.$v.$invalid) {
                    e.preventDefault()
                } else {
                    this.sending = true
                    try {
                        let formData = {
                            firstName: this.firstName,
                            lastName: this.lastName,
                            userEmail: this.userEmail,
                            password: this.password,
                            title: this.title,
                            jobTitle: null
                        }

                        if (this.jobTitle) {
                            formData.jobTitle = this.jobTitle.id
                        }

                        await this.$store.dispatch('User/sendAgreementRequestForNew', {
                            agreementForm: formData,
                            slug: this.$route.params.slug,
                        })
                        this.$store.dispatch('setSignedAgreement', true)
                        this.signed = true
                    } catch (err) {
                        const responseError = err.response ? err.response.data.error : null
                        this.handleError(err.code, responseError.message)
                    }
                }
            }
        },
        async created () {
            try {
                const agreementData = await this.$store.dispatch('User/loadAgreementForm', this.$route.params.slug)
                this.organizationLogoUrls = agreementData.organizationLogoUrls
                this.isAgreementForm = true
                this.jobTitles = await this.$store.dispatch('User/loadJobTitles')
                this.sortedJobTitles = this.sortAlphabeticallyAsc(this.jobTitles)
            } catch (exc) {
                this.isAgreementForm = false
                this.handleError(exc.code)
            }
        }
    }
</script>

<style lang="scss" scoped>
    .error {
        color: darkred;
        margin: 0 0 20px;
    }

    .agreement-form {
        &__intro-container {
            background: $bright_gray;
        }

        &__intro {
            max-width: 1300px;
            margin: 0 auto;
            width: 100%;
            display: flex;
            align-items: center;
            padding: 0 20px;
        }

        &__intro-company-logo {
            height: 100px;
            margin: 20px 30px 20px 0;
        }

        &__intro-description {
            font-size: 1.8rem;
            max-width: 900px;
        }
        &__container {
            display: flex;
            flex-direction: column;
            margin: 0 auto;
            justify-content: center;

            [class*="desktop"] & {
                flex-direction: row;
                padding: 80px 0 30px;
            }

            [class*="mobile"] & {
                padding: 30px;
            }
        }
        &__left-side,
        &__right-side {
            display: flex;
        }

        &__right-side {
            padding: 0 0 0 60px;
        }

        &__left-side {
            flex-direction: column;
            padding: 0;
            display: flex;

            [class*="desktop"] & {
                width: 750px;
            }
        }

        &__inner {
            display: flex;
            flex-wrap: wrap;
            gap: 18px;
            padding: 0 0 15px;
        }

        &__inputs {
            display: flex;
            flex-wrap: wrap;
            gap: 18px;
            padding: 0 20px;
        }

        &__input {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            [class*="desktop"] & {
                width: calc(50% - 10px);
            }

            .totem-input {
                border-color: $silver_chalice;
            }
        }

        &__header {
            margin: 0 0 20px;
            font-family: PPAgrandir-Medium, sans-serif;
            color: $lavender;
        }

        &__description {
            margin: 0 0 20px;
        }

        &__description-login-notification {
            display: flex;
            flex-direction: column;
            width: 1050px;
            background: $light_blue;
            padding: 20px;
            gap: 20px;
            border-radius: 6px;
            justify-self: center;
            margin: 0 0 80px 0;

            [class*="mobile"] & {
                width: 100%;
                margin: 30px;
            }
        }

        &__terms {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
        }

        &__terms-header {
            font-size: 1.3rem;
            font-family: PlusJakartaSans-Bold, sans-serif;
            margin: 40px 0 0;
        }

        &__terms-label::v-deep a {
            color: $lavender;
            cursor: pointer;
            text-decoration: underline;
        }

        &__terms-checkbox {
            margin-top: 4px !important;
        }

        &__faq-link {
            color: $lavender;
            cursor: pointer;
        }

        &__link {
            font-family: PlusJakartaSans-Medium, sans-serif;
            font-size: 1.8rem;
            height: 41px;
            background: $white;
            color: $stratos;
            border: 2px solid $lavender;
            border-radius: 30px;
            padding: 0 25px;
            line-height: 38px;
            cursor: pointer;
            width: max-content;

            &:hover,
            &:active {
                background: $stratos;
                color: $white;
                border: 2px solid $stratos;
            }
        }

        &__change-pass-button {
            margin-top: 10px;
            display: block;
        }

        &__thank-you {
            max-width: 400px;
            font-size: 3rem;
            margin: 0 0 40px;
        }

        &__submit-button {
            margin: 0 0 20px;
        }

        &__image {
            height: 465px;
        }

        &__description-login-notification-title {
            font-size: 2.2rem;
        }
    }
</style>
